/** Sherry's Wardrobe — isolated CULT-style glass navigation (no theme JS edits). */
(() => {
  if (window.__sherrysGlassMenuInitialized) return;
  window.__sherrysGlassMenuInitialized = true;
  const instances = new Map();
  let openInstance = null;
  let pageLock = null;

  function lockPage() {
    if (pageLock) return;
    const page = document.querySelector('.page-wrapper');
    pageLock = {
      html: document.documentElement.style.overflow,
      body: document.body.style.overflow,
      page,
      pageOverflow: page?.style.overflow || ''
    };
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    if (page) page.style.overflow = 'hidden';
  }

  function unlockPage() {
    if (!pageLock) return;
    document.documentElement.style.overflow = pageLock.html;
    document.body.style.overflow = pageLock.body;
    if (pageLock.page?.isConnected) pageLock.page.style.overflow = pageLock.pageOverflow;
    pageLock = null;
  }

  function positionOverlay(instance) {
    const header = document.querySelector('#header-component');
    const bottom = header?.getBoundingClientRect().bottom ?? 0;
    const top = Math.max(0, Math.min(window.innerHeight, Math.round(bottom)));
    instance.root.style.setProperty('--swg-header-offset', `${top}px`);
  }

  function setActive(instance, index) {
    instance.root.querySelectorAll('[data-swg-select]').forEach(button => {
      const active = button.dataset.swgSelect === String(index);
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-expanded', String(active));
    });
    instance.root.querySelectorAll('[data-swg-submenu]').forEach(panel => {
      panel.hidden = panel.dataset.swgSubmenu !== String(index);
    });
    instance.root.querySelectorAll('[data-swg-preview]').forEach(panel => {
      panel.hidden = panel.dataset.swgPreview !== String(index);
    });
  }

  function focusable(instance) {
    const scope = instance.isCountryPickerOpen?.()
      ? instance.root.querySelector('[data-swg-country-picker]')
      : instance.root;
    return [...(scope || instance.root).querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), summary, [tabindex="0"]')]
      .filter(el => el.getClientRects().length && el.getAttribute('aria-hidden') !== 'true');
  }

  function initCountrySelector(instance) {
    const root = instance.root;
    const picker = root.querySelector('[data-swg-country-picker]');
    const trigger = root.querySelector('[data-swg-country-open]');
    const search = root.querySelector('[data-swg-country-search]');
    const empty = root.querySelector('[data-swg-country-empty]');
    const form = root.querySelector('.swg-country-form');
    const input = root.querySelector('[data-swg-country-input]');
    if (!picker || !trigger || !search || !form || !input) return;
    const options = [...picker.querySelectorAll('[data-swg-country-option]')];
    const signal = instance.controller.signal;

    const filter = () => {
      const query = search.value.trim().toLocaleLowerCase();
      let found = 0;
      for (const option of options) {
        const matches = !query || (option.dataset.swgCountrySearch || '').includes(query);
        option.hidden = !matches;
        if (matches) found++;
      }
      if (empty) empty.hidden = found > 0;
    };

    instance.isCountryPickerOpen = () => !picker.hidden;
    instance.closeCountryPicker = (restoreFocus = true) => {
      if (picker.hidden) return;
      picker.hidden = true;
      trigger.setAttribute('aria-expanded', 'false');
      if (restoreFocus && trigger.isConnected) trigger.focus({ preventScroll: true });
    };

    trigger.addEventListener('click', () => {
      picker.hidden = false;
      trigger.setAttribute('aria-expanded', 'true');
      search.value = '';
      filter();
      requestAnimationFrame(() => search.focus({ preventScroll: true }));
    }, { signal });
    root.querySelectorAll('[data-swg-country-close]').forEach(button => {
      button.addEventListener('click', () => instance.closeCountryPicker(), { signal });
    });
    search.addEventListener('input', filter, { signal });
    options.forEach(button => button.addEventListener('click', () => {
      const iso = button.dataset.swgCountryCode;
      if (!iso || !/^[A-Z]{2}$/.test(iso)) return;
      if (input.value === iso) { instance.closeCountryPicker(); return; }
      input.value = iso;
      button.disabled = true;
      if (typeof form.requestSubmit === 'function') form.requestSubmit();
      else form.submit();
    }, { signal }));
  }

  function close(instance, restoreFocus = true) {
    if (!instance?.isOpen) return;
    instance.isOpen = false;
    instance.closeCountryPicker?.(false);
    instance.trigger.setAttribute('aria-expanded', 'false');
    instance.root.classList.remove('is-open');
    document.documentElement.classList.remove('swg-is-open');
    if (openInstance === instance) openInstance = null;
    unlockPage();
    const duration = Math.max(0, Math.min(1000, Number(instance.root.dataset.swgDuration) || 350));
    clearTimeout(instance.hideTimer);
    instance.hideTimer = setTimeout(() => {
      if (!instance.isOpen) instance.root.hidden = true;
    }, window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : duration + 25);
    if (restoreFocus && instance.trigger.isConnected) instance.trigger.focus({ preventScroll: true });
  }

  function open(instance) {
    if (instance.isOpen) return;
    if (openInstance && openInstance !== instance) close(openInstance, false);
    clearTimeout(instance.hideTimer);
    instance.root.hidden = false;
    instance.isOpen = true;
    openInstance = instance;
    lockPage();
    positionOverlay(instance);
    instance.trigger.setAttribute('aria-expanded', 'true');
    document.documentElement.classList.add('swg-is-open');
    requestAnimationFrame(() => {
      if (!instance.isOpen) return;
      instance.root.classList.add('is-open');
      instance.root.querySelector('.swg-close')?.focus({ preventScroll: true });
    });
  }

  function keyboardHandler(instance, event) {
    if (!instance.isOpen) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      if (instance.isCountryPickerOpen?.()) instance.closeCountryPicker();
      else close(instance);
      return;
    }
    if (event.key !== 'Tab') return;
    const targets = focusable(instance);
    if (!targets.length) { event.preventDefault(); return; }
    const first = targets[0], last = targets[targets.length - 1];
    if (event.shiftKey && (document.activeElement === first || !instance.root.contains(document.activeElement))) {
      event.preventDefault(); last.focus();
    } else if (!event.shiftKey && (document.activeElement === last || !instance.root.contains(document.activeElement))) {
      event.preventDefault(); first.focus();
    }
  }

  function dispose(instance) {
    if (instance.isOpen) close(instance, false);
    clearTimeout(instance.hideTimer);
    instance.controller.abort();
    instance.root.remove();
    instances.delete(instance.id);
    if (instances.size === 0) document.documentElement.classList.remove('swg-ready');
  }

  function initialize() {
    document.querySelectorAll('[data-swg-root]').forEach(root => {
      const id = root.dataset.swgRoot;
      const trigger = [...document.querySelectorAll('[data-swg-trigger]')]
        .find(button => button.dataset.swgTrigger === id);
      if (!id || !trigger) return;
      if (instances.get(id)?.trigger === trigger && instances.get(id)?.root === root) return;
      if (instances.has(id)) dispose(instances.get(id));

      const controller = new AbortController();
      const signal = controller.signal;
      const instance = { id, root, trigger, controller, hideTimer: null, isOpen: false };
      instances.set(id, instance);
      initCountrySelector(instance);
      // Body portal avoids Horizon sticky header's stacking/contain constraints.
      document.body.appendChild(root);

      trigger.addEventListener('click', () => instance.isOpen ? close(instance) : open(instance), { signal });
      root.querySelectorAll('[data-swg-close]').forEach(button => {
        button.addEventListener('click', () => close(instance), { signal });
      });
      root.querySelectorAll('[data-swg-select]').forEach(button => {
        button.addEventListener('click', () => setActive(instance, button.dataset.swgSelect), { signal });
      });
      document.addEventListener('keydown', e => keyboardHandler(instance, e), { signal });
      window.addEventListener('resize', () => {
        if (instance.isOpen) positionOverlay(instance);
        if (window.innerWidth >= 750 && instance.isCountryPickerOpen?.()) instance.closeCountryPicker(false);
      }, { signal });
      window.addEventListener('scroll', () => { if (instance.isOpen) positionOverlay(instance); }, { capture: true, passive: true, signal });
      root.querySelector('.swg-backdrop')?.addEventListener('wheel', e => e.preventDefault(), { passive: false, signal });

      // Maintain keyboard focus within the modal if focus is sent outside.
      document.addEventListener('focusin', e => {
        if (instance.isOpen && !root.contains(e.target) && e.target !== trigger) {
          root.querySelector('.swg-close')?.focus({ preventScroll: true });
        }
      }, { signal });
      document.documentElement.classList.add('swg-ready');
    });
  }

  document.addEventListener('shopify:section:unload', event => {
    for (const instance of [...instances.values()]) {
      if (event.target?.contains(instance.trigger)) dispose(instance);
    }
  });
  // Header actions keep working: close the modal before letting Horizon handle the click.
  document.addEventListener('click', event => {
    if (!openInstance || !(event.target instanceof Element)) return;
    if (event.target.closest('#header-component') && !event.target.closest('[data-swg-trigger]')) {
      close(openInstance, false);
    }
  }, true);

  // Horizon can hydrate/replace header markup after initial load.
  // Watch only for relevant menu/header additions and removals to rebind safely.
  const observer = new MutationObserver(records => {
    const relevant = records.some(record => [...record.addedNodes, ...record.removedNodes].some(node => {
      if (!(node instanceof Element)) return false;
      return node.matches('[data-swg-root], [data-swg-trigger], #header-component, .header-section') ||
        !!node.querySelector('[data-swg-root], [data-swg-trigger], #header-component');
    }));
    if (!relevant) return;
    for (const instance of [...instances.values()]) {
      if (!instance.trigger.isConnected) dispose(instance);
    }
    initialize();
  });
  // Horizon header hydration happens inside #header-group. Observing all of <body>
  // reacts to unrelated cart, video, app and product-grid mutations.
  function observeHeaderGroup() {
    const headerGroup = document.getElementById('header-group');
    if (headerGroup) observer.observe(headerGroup, { childList: true, subtree: true });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', observeHeaderGroup, { once: true });
  } else observeHeaderGroup();

  document.addEventListener('shopify:section:load', initialize);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
  else initialize();
})();
